import React, { useState } from 'react';
import Button from '../common/Button.js';
import SelectField from '../common/SelectField.js';
import SearchableSelectField from '../common/SearchableSelectField.js';
import useAppStore from '../../store/useAppStore.js';
import TokenPreview from '../common/TokenPreview.js';

const ReceiptSidebar = ({
  receiptItems,
  selectedPending,
  setSelectedPending,
  pendingOptions,
  handleAddSelected,
  onTransferToTable,
  discount,
  setDiscount,
  receivedCash,
  setReceivedCash,
  balance,
  saleError,
  saleMessage,
  handleSave,
  handlePrintBill,
  handlePrintOnly,
  handleClear,
  printMode = 'both',
  setPrintMode,
  waiters = [],
  selectedWaiterId,
  onChangeWaiter,
  waiterOrderOptions = [],
  selectedWaiterOrderId,
  onChangeWaiterOrder,
  onPrintWaiterOrder,
  onAddPendingWaiterOrder,
  onAddToWaiterOrder,
  branchReceiptData,
  showPrintOnly = false,
  editingOrder,
  handleUpdateOrder,
  isSubmitting = false,
  isPrintPending = false
}) => {
  const [selectedTableForTransfer, setSelectedTableForTransfer] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const subtotal = receiptItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountValue = Math.min(Number(discount) || 0, subtotal);
  const grandTotal = subtotal - discountValue;

  const { branchTables } = useAppStore((state) => ({
    branchTables: state.branchTables,
  }));

  const handleWaiterSearchChange = (e) => {
    if (e?.target?.value !== undefined) {
      onChangeWaiter && onChangeWaiter(e.target.value);
    }
  };

  const handleWaiterOrderSearchChange = (e) => {
    if (e?.target?.value !== undefined) {
      onChangeWaiterOrder && onChangeWaiterOrder(e.target.value);
    }
  };

  const handleTableSearchChange = (e) => {
    if (e?.target?.label) {
      setSelectedTableForTransfer(e.target.value);
    }
  };

  const handleSaveWithPayment = () => {
    handleSave({ paymentMethod });
  };

  const handlePrintWithPayment = () => {
    handlePrintBill({ paymentMethod });
  };

  const handlePrintOnlyWithPayment = () => {
    handlePrintOnly && handlePrintOnly({ paymentMethod });
  };

  return (
    <div className="rounded-3xl text-xl border border-gray-200 bg-white p-4 shadow-sm">
      {/*       
      // <div className="mt-4 h-[400px] overflow-y-scroll gap-2 bg-gray-200 flex flex-col items-center py-2" >
      //   <ReceiptPreview receiptItems={receiptItems} discount={discount} selectedTable={selectedPending} tables={tables} />
      //   <TokenPreview receiptItems={receiptItems} discount={discount} selectedTable={selectedPending} />
      //   </div> */}
      {saleError && <div className="mt-4 rounded-2xl bg-red-50 p-3 text-sm text-red-700">{saleError}</div>}
      {saleMessage && <div className="mt-4 rounded-2xl bg-green-50 p-3 text-sm text-green-700">{saleMessage}</div>}
      {isPrintPending && <div className="mt-4 rounded-2xl bg-yellow-50 p-3 text-sm text-yellow-700">Complete or cancel the print dialog before submitting another order.</div>}
      <div className='flex justify-between items-start w-full gap-4'>
        <div>
          <div className="mt-6 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Total</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={grandTotal.toFixed(2)}
                readOnly
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Print Type</label>
              <select
                value={printMode}
                onChange={(e) => setPrintMode && setPrintMode(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="both">Both</option>
                <option value="receipt">Receipt</option>
                <option value="token">Token</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Discount {branchReceiptData?.enableAutoDiscount ? '(Auto)' : '(Manual)'}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discount}
                onChange={(e) => !branchReceiptData?.enableAutoDiscount && setDiscount(e.target.value)}
                readOnly={branchReceiptData?.enableAutoDiscount}
                className={`w-full rounded-2xl border px-4 py-3 outline-none ${
                  branchReceiptData?.enableAutoDiscount
                    ? 'border-gray-300 bg-gray-50 text-gray-600'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-100'
                }`}
                placeholder={branchReceiptData?.enableAutoDiscount ? 'Auto-applied' : 'Discount amount'}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cash received</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={receivedCash}
                onChange={(e) => setReceivedCash(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                placeholder="Amount tendered"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">Jazz Cash</option>
                <option value="voucher">Easy Paisa</option>
              </select>
              <div className="flex justify-between text-sm font-semibold text-gray-700">
                <span>Change due</span>
                <span className={balance < 0 ? 'text-red-600 text-lg' : 'text-green-700 text-lg'}>PKR {balance.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-3">
              {editingOrder ? (
                <>
                  <Button variant="primary" onClick={handleUpdateOrder} disabled={isSubmitting || isPrintPending || receiptItems.length === 0}>
                    Update Order
                  </Button>
                  <Button variant="outline" onClick={handleClear} disabled={isSubmitting || isPrintPending}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={handleSaveWithPayment} disabled={isSubmitting || isPrintPending || receiptItems.length === 0 || !paymentMethod}>
                    Save
                  </Button>
                  <Button variant="primary" onClick={handlePrintWithPayment} disabled={isSubmitting || isPrintPending || receiptItems.length === 0 || !paymentMethod}>
                    Print & Save
                  </Button>
                  {showPrintOnly && (
                    <Button variant="primary" onClick={handlePrintOnlyWithPayment} disabled={isSubmitting || isPrintPending || receiptItems.length === 0 || !paymentMethod}>
                      Print only
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleClear} disabled={isSubmitting || isPrintPending}>
                    Clear
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <div>

          {/* // This section is for to add Items to the tables */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 my-4">
            <div>
              <SearchableSelectField
                id="selectTableTransfer"
                name="selectTableTransfer"
                label="Select Table"
                options={branchTables.map((option) => ({
                  label: option.name || option.tableName || option.tableId || option.value,
                  value: option.id || option.tableId || option.value
                }))}
                value={selectedTableForTransfer}
                placeholder="Search table..."
                onChange={handleTableSearchChange}
              />
            </div>
            <div className="flex items-end justify-end">
              <Button variant="primary" onClick={() => onTransferToTable && onTransferToTable(selectedTableForTransfer)} disabled={!selectedTableForTransfer || receiptItems.length === 0}>
                Add To Table
              </Button>
            </div>
          </div>

          {/* This section is for creating or adding to a waiter order */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 my-4">
            <div>
              <SearchableSelectField
                id="selectWaiter"
                name="selectWaiter"
                label="Select Waiter"
                options={waiters.length > 0 ? waiters.map((waiter) => ({
                  label: waiter.name || waiter.id,
                  value: String(waiter.id)
                })) : [{
                  label: 'No waiters available',
                  value: ''
                }]}
                value={selectedWaiterId}
                placeholder="Search waiter..."
                onChange={handleWaiterSearchChange}
              />
            </div>
            <div className="flex items-end justify-end">
              <Button 
                variant="primary" 
                onClick={() => onAddPendingWaiterOrder && onAddPendingWaiterOrder(selectedWaiterId)} 
                disabled={isPrintPending || !selectedWaiterId || receiptItems.length === 0}
                title="Create new waiter order and issue token"
              >
                Issue Token
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 my-4">
            <div>
              <SearchableSelectField
                id="selectWaiterOrder"
                name="selectWaiterOrder"
                label="Select Waiter Order"
                options={waiterOrderOptions.length > 0 ? waiterOrderOptions : [{ label: 'No existing waiter orders', value: '' }]}
                value={selectedWaiterOrderId}
                placeholder="Search waiter order..."
                onChange={handleWaiterOrderSearchChange}
              />
            </div>
            <div className="flex items-end justify-end">
              <Button
                variant="secondary"
                onClick={() => onAddToWaiterOrder && onAddToWaiterOrder(selectedWaiterOrderId)}
                disabled={!selectedWaiterOrderId || receiptItems.length === 0}
                title="Add current receipt items to the selected waiter order"
              >
                Add to Order
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptSidebar;
